import { tr } from '@/lib/i18n-core';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DAY_CLOSES_AT_MAX_HOUR } from '@his/shared';
import { CalendarClock, UserRound } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { dateTime, display } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Field, FormSkeleton, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { scheduleAccess, selectableDoctors } from './schedule-access';
import { safeReturnTo } from './BookAppointmentPage';
import { ClinicHoursTable } from './ClinicHoursTable';
import {
  CLINIC_HOURS_PAGE,
  departmentFor,
  doctorsInDepartment,
  doctorsOfBranch,
  doctorsWithoutDepartment,
  departmentsOf,
  mergeDepartments,
  pickOne,
  emptyWeek,
  rowForDepartment,
  slotsFromWeek,
  weekFromSlots,
  weekIssue,
  weekdayLabel,
  type ClinicHoursDoctor,
  type ClinicHoursRow,
  type WeekHours,
} from './clinic-hours-model';

interface OutsideBooking {
  appointmentId: string;
  slotAt: string;
  patientName: string;
  mrn: string;
  reason: string;
}

interface PublishResult {
  outsideTemplate?: OutsideBooking[];
  outsideTemplateTruncated?: boolean;
}

const OUTSIDE_REASON: Record<string, string> = {
  no_clinic_that_day: 'No clinic that day',
  outside_hours: 'Outside the clinic hours',
  off_grid: 'Not one of the new slot times',
};

export function SchedulePublishPage() {
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const [params] = useSearchParams();
  const { can, user } = useAuth();
  const workingBranchId = useWorkingBranchId();
  const returnTo = safeReturnTo(params.get('returnTo'), '/schedules');

  const access = scheduleAccess(
    { all: can('schedule.manage'), own: can('schedule.manage.own') },
    user?.id ?? null,
  );
  const self: ClinicHoursDoctor | null = user ? { id: user.id, name: user.displayName } : null;

  const doctors = useQuery({
    queryKey: ['bookable-doctors'],
    queryFn: async () =>
      (await get<{ data: ClinicHoursDoctor[] }>('/org/clinicians?kind=bookable&limit=200')).data,
  });
  const branchDepartments = useQuery({
    queryKey: ['departments'],
    queryFn: () => get<{ id: string; name: string; branchId: string | null; kind?: string }[]>('/org/departments'),
    enabled: can('department.read'),
  });
  const opened = useQuery({
    queryKey: ['schedules', 'one', scheduleId],
    queryFn: () => get<ClinicHoursRow[]>(`/schedules${qs({ id: scheduleId, limit: 1 })}`),
    enabled: Boolean(scheduleId),
  });

  const editing = scheduleId ? (opened.data?.[0] ?? null) : null;
  const title = editing
    ? tr('opd', 'edit_clinic_hours_x', { name: editing.doctor.name })
    : tr('opd', 'publish_clinic_hours');
  const loading = doctors.isLoading || (Boolean(scheduleId) && opened.isLoading);

  if (loading) {
    return (
      <FormScreen title={title} icon={CalendarClock} backTo={returnTo}>
        <FormSkeleton />
      </FormScreen>
    );
  }

  if (scheduleId && !editing) {
    return (
      <FormScreen title={title} icon={CalendarClock} backTo={returnTo}>
        <Alert variant="destructive">
          {opened.error ? describeError(opened.error) : tr('opd', 'schedule_not_found')}
        </Alert>
      </FormScreen>
    );
  }

  const branch = user?.branches?.find(
    (candidate) => candidate.id === (editing?.branchId ?? workingBranchId),
  );
  const forBranch = doctorsOfBranch(doctors.data ?? []);
  const offered = selectableDoctors(access, forBranch, self);
  const withEditing =
    editing && !offered.some((doctor) => doctor.id === editing.doctorId)
      ? [
          ...offered,
          (doctors.data ?? []).find((doctor) => doctor.id === editing.doctorId) ?? {
            id: editing.doctorId,
            name: editing.doctor.name,
          },
        ]
      : offered;
  const unplaced = editing
    ? []
    : selectableDoctors(access, doctorsWithoutDepartment(doctors.data ?? []), null);

  return (
    <ClinicHoursForm
      key={editing?.id ?? 'new'}
      editing={editing}
      doctors={withEditing}
      unplaced={unplaced}
      branchDepartments={(branchDepartments.data ?? []).filter(
        (department) =>
          department.branchId === (editing?.branchId ?? workingBranchId) && (department.kind ?? 'clinical') === 'clinical',
      )}
      excludedDepartmentIds={
        new Set(
          (branchDepartments.data ?? [])
            .filter((department) => (department.kind ?? 'clinical') !== 'clinical')
            .map((department) => department.id),
        )
      }
      branchId={editing?.branchId ?? workingBranchId}
      branchName={branch?.name ?? '—'}
      returnTo={returnTo}
      title={title}
    />
  );
}

function ClinicHoursForm({
  editing,
  doctors,
  unplaced,
  branchDepartments,
  excludedDepartmentIds,
  branchId,
  branchName,
  returnTo,
  title,
}: {
  editing: ClinicHoursRow | null;
  doctors: ClinicHoursDoctor[];
  unplaced: ClinicHoursDoctor[];
  branchDepartments: { id: string; name: string }[];
  excludedDepartmentIds: ReadonlySet<string>;
  branchId: string;
  branchName: string;
  returnTo: string;
  title: string;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [chosenDoctor, setChosenDoctor] = useState(editing?.doctorId ?? '');
  const [chosenDepartment, setChosenDepartment] = useState(editing?.departmentId ?? '');
  const [edited, setEdited] = useState<{ scope: string; week: WeekHours } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outside, setOutside] = useState<{ bookings: OutsideBooking[]; truncated: boolean } | null>(
    null,
  );

  const editingDoctor = editing
    ? doctors.find((candidate) => candidate.id === editing.doctorId)
    : undefined;
  const departments = editing
    ? (editingDoctor?.departments ?? [])
    : mergeDepartments(branchDepartments, departmentsOf(doctors)).filter(
        (department) => !excludedDepartmentIds.has(department.id),
      );
  const departmentId = editing
    ? departmentFor(editingDoctor, chosenDepartment)
    : pickOne(departments, chosenDepartment);
  const doctorOptions = editing
    ? doctors.filter((candidate) => candidate.id === editing.doctorId)
    : doctorsInDepartment(doctors, departmentId);
  const doctorId = editing ? editing.doctorId : pickOne(doctorOptions, chosenDoctor);
  const legacy = Boolean(editing) && editing?.departmentId === null;
  const scope = `${doctorId}|${departmentId}`;

  const existing = useQuery({
    queryKey: ['schedules', 'doctor', doctorId, branchId],
    queryFn: () =>
      get<ClinicHoursRow[]>(`/schedules${qs({ doctorId, branchId, limit: CLINIC_HOURS_PAGE })}`),
    enabled: !editing && Boolean(doctorId) && Boolean(branchId),
  });

  const baseRow =
    editing ??
    (departmentId && existing.data ? rowForDepartment(existing.data, departmentId) : null);
  const base = useMemo(() => (baseRow ? weekFromSlots(baseRow.slots) : emptyWeek()), [baseRow]);
  const week = edited && edited.scope === scope ? edited.week : base;
  const waiting = !editing && Boolean(doctorId) && Boolean(departmentId) && existing.isLoading;

  const issue = useMemo(() => weekIssue(week, DAY_CLOSES_AT_MAX_HOUR), [week]);
  const ready =
    Boolean(doctorId) && Boolean(departmentId) && Boolean(branchId) && !issue && !waiting;
  const dirty =
    edited !== null &&
    edited.scope === scope &&
    JSON.stringify(slotsFromWeek(week)) !== JSON.stringify(slotsFromWeek(base));

  const finish = () => {
    void queryClient.invalidateQueries({ queryKey: ['schedules'] });
    navigate(returnTo, { replace: true });
  };

  const save = useMutation({
    mutationFn: () =>
      post<PublishResult>('/schedules', {
        doctorId,
        branchId,
        departmentId,
        ...(legacy && editing ? { replacesScheduleId: editing.id } : {}),
        isActive: true,
        slots: slotsFromWeek(week),
      }),
    onSuccess: (result) => {
      toast.success(
        tr('opd', 'clinic_hours_published'),
        tr('opd', 'the_booking_screen_can_now_offer_these_slots'),
      );
      if (result.outsideTemplate?.length) {
        setOutside({
          bookings: result.outsideTemplate,
          truncated: Boolean(result.outsideTemplateTruncated),
        });
        return;
      }
      finish();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const issueText = issueMessage(issue);
  const showEditor = Boolean(doctorId) && Boolean(departmentId);

  return (
    <FormScreen
      title={title}
      description={tr('opd', 'clinic_hours_replace_note')}
      icon={CalendarClock}
      backTo={returnTo}
      dirty={dirty && !save.isSuccess}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('opd', 'doctor')} icon={UserRound}>
        <Field label={tr('opd', 'branch')}>
          <p className="flex h-9 items-center text-sm font-medium">{branchName}</p>
        </Field>
        <Field
          label={tr('opd', 'department')}
          required
          hint={legacy ? tr('opd', 'pick_department_for_old_hours') : undefined}
        >
          <Select
            value={departmentId}
            disabled={Boolean(editing) && !legacy}
            onChange={(event) => setChosenDepartment(event.target.value)}
          >
            <option value="">{tr('opd', 'select_a_department')}</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={tr('opd', 'doctor')}
          required
          hint={editing ? tr('opd', 'locked_while_editing_start_a_new_schedule_to') : undefined}
        >
          <Select
            value={doctorId}
            disabled={Boolean(editing) || !departmentId}
            onChange={(event) => setChosenDoctor(event.target.value)}
          >
            <option value="">
              {!departmentId
                ? tr('opd', 'choose_the_department_first')
                : doctorOptions.length === 0
                  ? tr('opd', 'no_doctor_in_this_department_takes_appointments')
                  : tr('opd', 'select_a_doctor')}
            </option>
            {doctorOptions.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </Select>
        </Field>
        {(unplaced.length > 0 || departments.length === 0) && (
          <div className="sm:col-span-2">
            <Alert variant="warning">
              {unplaced.length > 0
                ? tr('opd', 'doctors_without_department_x', {
                    names: unplaced.map((candidate) => candidate.name).join(', '),
                  })
                : tr('opd', 'doctor_has_no_department')}{' '}
              <Link to="/people" className="font-medium underline">
                {tr('opd', 'open_staff_and_roles')}
              </Link>
            </Alert>
          </div>
        )}
      </FormSection>

      <FormSection title={tr('opd', 'weekly_hours')} icon={CalendarClock} columns={1}>
        {!showEditor && (
          <p className="text-sm text-muted-foreground">
            {tr('opd', 'choose_doctor_and_department_first')}
          </p>
        )}
        {showEditor && waiting && <FormSkeleton />}
        {showEditor && !waiting && (
          <ClinicHoursTable
            week={week}
            dayClosesAt={DAY_CLOSES_AT_MAX_HOUR}
            onChange={(next) => setEdited({ scope, week: next })}
          />
        )}
        {showEditor && !waiting && issueText && (
          <p
            className={
              issue?.kind === 'nothing_working'
                ? 'text-sm text-muted-foreground'
                : 'text-sm text-destructive'
            }
          >
            {issueText}
          </p>
        )}
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={tr('opd', 'publish')}
        loading={save.isPending}
        incomplete={!ready}
        cancelTo={returnTo}
      />

      {outside && (
        <Dialog
          open
          onClose={finish}
          wide
          title={tr('opd', 'some_booked_appointments_no_longer_fit')}
          description={tr('opd', 'the_new_hours_are_published_these_bookings_were')}
          footer={
            <Button type="button" onClick={finish}>
              {tr('opd', 'done')}
            </Button>
          }
        >
          <div className="space-y-3">
            <Alert variant="warning">
              {outside.bookings.length}
              {outside.truncated ? '+' : ''}{' '}
              {tr('opd', 'booking_s_fall_outside_the_new_hours_call_each')}
            </Alert>
            <ul className="max-h-72 divide-y overflow-y-auto rounded-md border text-sm">
              {outside.bookings.map((booking) => (
                <li
                  key={booking.appointmentId}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <span>
                    <span className="font-medium">{booking.patientName}</span>{' '}
                    <span className="text-xs text-muted-foreground">{booking.mrn}</span>
                  </span>
                  <span className="text-end">
                    <span className="tabular">{dateTime(booking.slotAt)}</span>
                    <span className="block text-xs text-muted-foreground">
                      {OUTSIDE_REASON[booking.reason] ?? booking.reason}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Dialog>
      )}
    </FormScreen>
  );
}

function issueMessage(issue: ReturnType<typeof weekIssue>): string | null {
  if (!issue) return null;
  if (issue.kind === 'nothing_working') return tr('opd', 'turn_on_a_working_day');
  if (issue.kind === 'ends_before_start')
    return tr('opd', 'a_session_must_end_after_it_starts_an_evening');
  return tr('opd', 'periods_overlap_on_day', {
    day: weekdayLabel(issue.weekday, display.locale, 'long'),
    first: issue.first,
    second: issue.second,
  });
}
